import React from "react";
import { getYear } from "../helper/helper";
import { PlayIcon, PauseIcon, MusicalNoteIcon } from "@heroicons/react/24/solid";
import { getItemAccessType } from "../helper/accessTypeHelper";

export default function PlaylistItem({
  index = 0,
  id,
  image,
  title,
  name,
  publish_date,
  released,
  cast,
  author,
  artist,
  duration,
  duration_time,
  price,
  exclusive_content,
  ppv_content,
  isActive = false,
  isPlaying = false,
  onTrackClick,
  dark = true,
}) {
  const songTitle = title || name || "Track Title";
  const songArtist = author || artist || cast || "Unknown Artist";
  const songYear = getYear(publish_date || released || "2023");
  const songDuration = duration || duration_time || "3:30";

  const access = getItemAccessType({
    price,
    exclusive_content,
    ppv_content,
  });

  const handleRowClick = () => {
    if (onTrackClick) {
      onTrackClick({
        id,
        image,
        title: songTitle,
        author: songArtist,
        price,
        exclusive_content,
        ppv_content,
        duration: songDuration,
      });
    }
  };

  return (
    <tr
      onClick={handleRowClick}
      className={`group cursor-pointer transition-all duration-200 select-none ${
        dark
          ? `hover:bg-zinc-850/80 ${
              isActive
                ? "bg-zinc-800/60 border-l-4 border-secondary text-secondary"
                : "bg-zinc-900/40 text-zinc-300"
            }`
          : `hover:bg-orange-50/70 ${
              isActive
                ? "bg-orange-50 border-l-4 border-secondary text-secondary font-semibold"
                : "bg-white text-zinc-800"
            }`
      } border-b ${dark ? "border-zinc-800/60" : "border-gray-100"}`}
    >
      {/* Index & Play Hover Trigger */}
      <td className="px-3 sm:px-4 py-3 text-center text-xs font-semibold w-10 sm:w-14">
        <div className="flex items-center justify-center">
          {isActive && isPlaying ? (
            <div className="flex items-end gap-[2px] h-4 w-4 pb-0.5">
              <span className="w-1 bg-secondary rounded-full animate-bounce h-3"></span>
              <span className="w-1 bg-secondary rounded-full animate-bounce h-4 delay-100"></span>
              <span className="w-1 bg-secondary rounded-full animate-bounce h-2 delay-200"></span>
            </div>
          ) : (
            <span
              className={`group-hover:hidden font-mono text-xs ${
                isActive ? "text-secondary font-bold" : "text-zinc-500 font-medium"
              }`}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
          )}
          <span className="hidden group-hover:flex items-center justify-center text-secondary">
            {isActive && isPlaying ? (
              <PauseIcon className="w-4 h-4 fill-secondary" />
            ) : (
              <PlayIcon className="w-4 h-4 fill-secondary" />
            )}
          </span>
        </div>
      </td>

      {/* Title & Artwork */}
      <td className="px-3 sm:px-4 py-3 text-sm">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-lg overflow-hidden bg-zinc-950 border border-zinc-800 relative group/img shadow">
            {image ? (
              <img
                src={image}
                alt={songTitle}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-zinc-800 flex items-center justify-center text-secondary">
                <MusicalNoteIcon className="w-5 h-5" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
              {isActive && isPlaying ? (
                <PauseIcon className="w-5 h-5 text-white" />
              ) : (
                <PlayIcon className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`text-xs sm:text-sm font-bold truncate ${
                isActive ? "text-secondary" : dark ? "text-white" : "text-black"
              }`}
            >
              {songTitle}
            </p>
            <p className="text-[11px] sm:text-xs text-zinc-400 truncate">
              {songArtist}
            </p>
          </div>
        </div>
      </td>

      {/* Access Badge */}
      <td className="px-3 sm:px-4 py-3 text-center text-xs">
        <span
          className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${access.badgeClass}`}
        >
          {access.badgeText}
        </span>
      </td>

      {/* Release Year (Hidden on smaller mobile) */}
      <td className="hidden md:table-cell px-4 py-3 text-center text-xs text-zinc-400 font-medium">
        {songYear}
      </td>

      {/* Duration */}
      <td className="px-3 sm:px-4 py-3 text-right sm:text-center text-xs font-semibold text-zinc-400">
        {songDuration}
      </td>
    </tr>
  );
}
